import { DayDrivingTime } from './useDayDrivingTimes';

// A gap's standing relative to the day's break requirement at that point:
// - 'none'    : no break required yet, or rest already satisfied — neutral
// - 'covered' : a break is required and this gap is long enough to be it — good
// - 'short'   : a break is required, not yet met, and this gap is too short — warning
export type GapBreakStatus = 'none' | 'covered' | 'short';

export interface Gap {
  durationMinutes: number;
  /** Required cumulative break (min) at this point in the day. 0 = none required yet. */
  requiredBreakMinutes: number;
  breakStatus: GapBreakStatus;
  position: { lat: number; lng: number };
}

/**
 * Classifies the time gaps between consecutive assignments of a day.
 *
 * Pure function of the shared day-driving transform — no map/DOM — so the
 * gray/green/yellow logic can be unit-tested directly.
 *
 * Rules:
 * - Only gaps at least `minPauseMinutes` long are returned (shorter hand-offs
 *   aren't interesting).
 * - Only gaps between two real assignments (both with coordinates) are returned;
 *   assignment requests have no GPS/coords here.
 * - `breakStatus`:
 *   - 'none'    when no break is required yet at this point, OR the day's rest was
 *               already satisfied by the end of the previous assignment (e.g. a break
 *               taken within it or an earlier gap) — so this gap isn't needed as rest.
 *   - 'covered' when a break is still required and this single gap reaches it.
 *   - 'short'   when a break is still required and this gap is too short for it.
 */
export const classifyGaps = (
  dayDrivingTimes: DayDrivingTime[],
  minPauseMinutes: number
): Gap[] => {
  const result: Gap[] = [];

  dayDrivingTimes.forEach((entry, i) => {
    if (i === 0) return;
    const prev = dayDrivingTimes[i - 1].assignment?.assignment;
    const currA = entry.assignment?.assignment;
    if (!prev || !currA) return;

    const gapMs = entry.timeFromPreviousEnd;
    if (gapMs == null || gapMs <= 0) return;
    const durationMinutes = gapMs / 60000;
    if (durationMinutes < minPauseMinutes) return;

    if (
      prev.toLatitude == null ||
      prev.toLongitude == null ||
      currA.fromLatitude == null ||
      currA.fromLongitude == null
    ) {
      return;
    }

    const requiredBreak = entry.minRequiredCumulativeBreakMinutes ?? 0;
    // entry.hasEnoughBreakTimeUntilAssignmentEnd is the day's rest status BY THE END
    // of the assignment this gap leads into — and it already counts this gap as rest.
    // So if it's still false, the day's rest is missing even with this gap → the gap
    // was too short (warning). If it's true, rest is satisfied: green when this single
    // gap is itself long enough to be the required break, otherwise neutral (rest met
    // by accumulation / earlier breaks).
    const restMetAtEntry = entry.hasEnoughBreakTimeUntilAssignmentEnd === true;

    let breakStatus: GapBreakStatus;
    if (requiredBreak <= 0) {
      breakStatus = 'none';
    } else if (!restMetAtEntry) {
      breakStatus = 'short';
    } else if (durationMinutes >= requiredBreak) {
      breakStatus = 'covered';
    } else {
      breakStatus = 'none';
    }

    result.push({
      durationMinutes,
      requiredBreakMinutes: requiredBreak,
      breakStatus,
      position: {
        lat: (prev.toLatitude + currA.fromLatitude) / 2,
        lng: (prev.toLongitude + currA.fromLongitude) / 2,
      },
    });
  });

  return result;
};
